import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  TrendingUp,
  BookOpen,
  PenTool,
  Edit3,
  Target,
  Award,
} from "lucide-react";

const clamp = (n, min = 0, max = 100) =>
  Math.max(min, Math.min(max, Number.isFinite(n) ? n : 0));
const fmt = (n) => (n ?? 0).toLocaleString("es-ES");

/**
 * Props:
 *  - progreso (obj)
 *  - user (obj)
 *  - isAdmin (bool)
 *  - onGoLecciones? () => void
 *  - onGoEjercicios? () => void
 *  - onGoBiblioteca? () => void
 */
const ProgresoView = ({
  progreso,
  user,
  isAdmin = false,
  onGoLecciones,
  onGoEjercicios,
  onGoBiblioteca,
}) => {
  if (!progreso) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <TrendingUp className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold mb-2">
            Cargando tu progreso...
          </h3>
        </CardContent>
      </Card>
    );
  }

  // ⬇️ Clave: si es admin, tiene acceso total; también considera flags comunes de premium
  const hasPremiumAccess =
    isAdmin ||
    user?.tipo_cuenta === "premium" ||
    user?.is_premium === true ||
    user?.premium === true;

  // --- Nivel / XP ---
  const puntos = progreso?.nivel?.puntos ?? 0;
  const siguiente = progreso?.nivel?.siguiente ?? null;
  const pctNivel = siguiente
    ? clamp(Math.round((puntos / Math.max(1, siguiente)) * 100))
    : null;
  const faltan = siguiente ? Math.max(0, siguiente - puntos) : null;
  const tituloNivel = progreso?.nivel?.nivel || "Escritor Novato";

  // --- Lecciones ---
  const totalLecciones = progreso?.total_lecciones ?? 0;
  const compLecciones = progreso?.lecciones_completadas ?? 0;
  const pctLecciones =
    totalLecciones > 0
      ? clamp(Math.round((compLecciones / totalLecciones) * 100))
      : 0;

  // --- Ejercicios prácticos ---
  const totalRetos = progreso?.total_retos ?? 12;
  const compRetos = progreso?.retos_completados ?? 0;
  const pctRetos =
    totalRetos > 0 ? clamp(Math.round((compRetos / totalRetos) * 100)) : 0;

  // --- Otros KPIs ---
  const racha = progreso?.racha_actual ?? 0;
  const diasActivos = progreso?.dias_activos ?? 0;
  const escritosGuardados = progreso?.escritos_guardados ?? 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Mi Progreso</h2>
        <p className="text-gray-600">Sigue tu evolución como escritor</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Nivel y Experiencia */}
        <Card className="col-span-full lg:col-span-2">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-600" />
                  {tituloNivel}
                </CardTitle>
                <CardDescription>
                  {fmt(puntos)} puntos de experiencia
                  {siguiente && (
                    <> • {fmt(faltan)} puntos para el siguiente nivel</>
                  )}
                </CardDescription>
              </div>

              {isAdmin ? (
                <Badge
                  variant="secondary"
                  className="bg-orange-100 text-orange-800"
                >
                  Admin (todo desbloqueado)
                </Badge>
              ) : hasPremiumAccess ? (
                <Badge
                  variant="secondary"
                  className="bg-yellow-100 text-yellow-800"
                >
                  Premium activo
                </Badge>
              ) : (
                <Badge variant="outline">Gratis</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {siguiente ? (
              <>
                <Progress value={pctNivel ?? 0} className="w-full" />
                <div className="mt-2 text-xs text-gray-500">
                  {pctNivel}% del nivel actual
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-600">
                Has alcanzado el nivel máximo disponible 🎉
              </div>
            )}

            {/* CTA */}
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-amber-600">
                  {fmt(racha)}
                </div>
                <div className="text-sm text-gray-600">Racha actual</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">
                  {fmt(diasActivos)}
                </div>
                <div className="text-sm text-gray-600">Días activos</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">
                  {fmt(escritosGuardados)}
                </div>
                <div className="text-sm text-gray-600">Escritos guardados</div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {onGoLecciones && (
                <Button variant="outline" size="sm" onClick={onGoLecciones}>
                  Continuar lecciones
                </Button>
              )}
              {onGoEjercicios && hasPremiumAccess && (
                <Button variant="outline" size="sm" onClick={onGoEjercicios}>
                  Ir a ejercicios prácticos
                </Button>
              )}
              {onGoBiblioteca && escritosGuardados > 0 && (
                <Button variant="outline" size="sm" onClick={onGoBiblioteca}>
                  Ver mi biblioteca
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lecciones */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-600" />
              Lecciones
            </CardTitle>
            <CardDescription>
              {fmt(compLecciones)}/{fmt(totalLecciones)} completadas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {fmt(compLecciones)}/{fmt(totalLecciones)}
              </div>
              <Progress value={pctLecciones} className="w-full mb-2" />
              <div className="text-sm text-gray-600">
                {pctLecciones}% completado
              </div>
              {onGoLecciones && (
                <Button className="mt-3 w-full" onClick={onGoLecciones}>
                  Continuar lecciones
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ejercicios prácticos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-green-600" />
              Ejercicios prácticos
            </CardTitle>
            <CardDescription>
              {fmt(compRetos)}/{fmt(totalRetos)} completados
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {fmt(compRetos)}/{fmt(totalRetos)}
              </div>
              <Progress value={pctRetos} className="w-full mb-2" />
              <div className="text-sm text-gray-600">
                {hasPremiumAccess
                  ? "Progreso en ejercicios"
                  : "Actualiza a premium"}
              </div>

              {!hasPremiumAccess && (
                <Badge variant="outline" className="mt-2">
                  Bloqueado
                </Badge>
              )}

              {onGoEjercicios && hasPremiumAccess && (
                <Button className="mt-3 w-full" onClick={onGoEjercicios}>
                  Ir a ejercicios
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logros */}
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              Logros Desbloqueados
            </CardTitle>
            {progreso?.ultima_actividad && (
              <CardDescription>
                Última actividad:{" "}
                {new Date(progreso.ultima_actividad).toLocaleString("es-ES")}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {compLecciones >= 1 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <BookOpen className="h-8 w-8 text-yellow-600" />
                  </div>
                  <div className="text-sm font-medium">Primera Lección</div>
                  <div className="text-xs text-gray-600">
                    Completaste tu primera lección
                  </div>
                </div>
              )}

              {compLecciones >= 3 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="text-sm font-medium">
                    Estudiante Constante
                  </div>
                  <div className="text-xs text-gray-600">
                    3 lecciones completadas
                  </div>
                </div>
              )}

              {escritosGuardados >= 1 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Edit3 className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="text-sm font-medium">Primer Escrito</div>
                  <div className="text-xs text-gray-600">
                    Guardaste tu primer ejercicio
                  </div>
                </div>
              )}

              {racha >= 7 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Award className="h-8 w-8 text-purple-600" />
                  </div>
                  <div className="text-sm font-medium">Racha Semanal</div>
                  <div className="text-xs text-gray-600">
                    7 días seguidos activo
                  </div>
                </div>
              )}
            </div>

            {compLecciones === 0 && escritosGuardados === 0 && (
              <div className="text-center text-gray-500 py-8">
                <Target className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p>
                  Completa lecciones y escribe ejercicios para desbloquear
                  logros
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProgresoView;
